/**
 * Blank-print detector (print-reliability-1010, step 5 "MONITOR").
 *
 * A blank print throws nothing; before this, the only signal was a customer
 * silently reprinting (0 of 36 print incidents caught by us first). Every
 * in-app print goes through printInFrame, which measures the frame right
 * before print() and reports:
 *   print_attempt — every print: kind, route, doc id, tenant, frame_ok,
 *                   content_chars, visual_count, img_count/img_failed,
 *                   fonts_ms, ready_timed_out, superseded
 *   print_blank   — additionally, when isBlankPrint() says paper is empty.
 *
 * Privacy: counts and ids only. `doc` is a document id; no names, addresses,
 * tracking numbers or document text ever go into properties.
 */
import { capturePrintEvent } from "@/lib/analytics/posthog-events";
import { useAuthStore } from "@/lib/store/authStore";

export type PrintKind = "slip" | "label" | "pos_receipt" | "studio" | "pdf";

export interface PrintAnalytics {
  kind: PrintKind;
  /** Document id(s) only, e.g. "SO-2026-00012" or "SO-1,SO-2". */
  doc?: string;
}

export interface FrameMeasure {
  content_chars: number;
  /** Rendered visual elements: decoded images, svg, canvas. */
  visual_count: number;
  img_count: number;
  img_failed: number;
  /** The document wrapper has at least one element child. */
  has_elements: boolean;
  /** Measuring threw: the counts are unknown (never reported as blank). */
  measure_error: boolean;
}

export interface PrintMetrics extends FrameMeasure {
  frame_ok: boolean;
  fonts_ms: number;
  ready_timed_out: boolean;
  superseded: boolean;
}

/** Below this many text characters (and with no visuals) paper is blank. */
export const BLANK_TEXT_CHARS = 20;
const MAX_DOC_ID_CHARS = 200;

const EMPTY_MEASURE: FrameMeasure = {
  content_chars: 0,
  visual_count: 0,
  img_count: 0,
  img_failed: 0,
  has_elements: false,
  measure_error: false,
};

/** What the frame would put on paper, as counts. */
export function measureFrame(doc: Document): FrameMeasure {
  const body = doc.body;
  if (!body) return EMPTY_MEASURE;
  const images = Array.from(doc.images);
  const decoded = images.filter((img) => img.complete && img.naturalWidth > 0).length;
  const failed = images.filter((img) => img.complete && img.naturalWidth === 0).length;
  return {
    content_chars: (body.innerText ?? body.textContent ?? "").trim().length,
    visual_count: decoded + doc.querySelectorAll("svg, canvas").length,
    img_count: images.length,
    img_failed: failed,
    has_elements: !!body.firstElementChild?.firstElementChild,
    measure_error: false,
  };
}

/**
 * measureFrame that cannot throw. printInFrame measures right before print():
 * a throw there would skip the print, so telemetry would cause the very blank
 * print it exists to detect.
 */
export function safeMeasureFrame(doc: Document): FrameMeasure {
  try {
    return measureFrame(doc);
  } catch {
    return { ...EMPTY_MEASURE, measure_error: true };
  }
}

/**
 * Blank = nothing would reach paper. Image-only documents (Shopee/Lazada
 * sheets, barcode-only stickers) are NOT blank while their images render.
 */
export function isBlankPrint(m: PrintMetrics): boolean {
  if (!m.frame_ok) return true;
  if (m.measure_error) return false; // unknown is not blank
  if (!m.has_elements) return true;
  const noText = m.content_chars < BLANK_TEXT_CHARS;
  if (noText && m.visual_count === 0) return true;
  return noText && m.img_failed > 0;
}

/** Report one print. Fire-and-forget: analytics never breaks printing. */
export function reportPrint(analytics: PrintAnalytics | undefined, metrics: PrintMetrics): void {
  if (!analytics) return;
  try {
    const properties = {
      kind: analytics.kind,
      doc: analytics.doc?.slice(0, MAX_DOC_ID_CHARS),
      route: window.location.pathname,
      tenant: useAuthStore.getState().currentTenant ?? undefined,
      ...metrics,
    };
    capturePrintEvent("print_attempt", properties);
    if (!metrics.superseded && isBlankPrint(metrics)) capturePrintEvent("print_blank", properties);
  } catch {
    // Silent — analytics must never break printing.
  }
}

export { EMPTY_MEASURE };
